import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { ArrowLeft, User, Store, CheckCircle, AlertTriangle } from '../../lib/lucide-proxy';

const Register = () => {
  const [step, setStep] = useState(1); // 1: User Info, 2: Role, 3: Brewery Info
  const [role, setRole] = useState('customer');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    phone: '',
    brewery: {
      name: '',
      phone: '',
      address: '',
      description: ''
    }
  });
  const [loading, setLoading] = useState(false);
  const { signUp } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('brewery.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        brewery: {
          ...prev.brewery,
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      alert("As senhas não coincidem");
      return;
    }

    setLoading(true);
    
    // Prepare data structure
    const userData = {
      full_name: formData.full_name,
      phone: formData.phone,
      role: role,
      brewery: role === 'brewery_owner' ? formData.brewery : null
    };

    const { error } = await signUp(
      formData.email, 
      formData.password, 
      userData
    );

    setLoading(false);

    if (!error) {
      // Redirect to login or show success page
      navigate('/login');
    }
  };

  return (
    <>
      <Helmet>
        <title>Criar Conta - Cerveja Delivery</title>
      </Helmet>

      <div className="min-h-screen bg-cream-300 flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className="absolute top-[-10%] left-[-5%] w-96 h-96 bg-primary-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse-custom"></div>
        </div>

        <div className="sm:mx-auto sm:w-full sm:max-w-md relative z-10">
          <Link to="/" className="inline-flex items-center mb-8 text-dark-500 hover:text-primary-500 transition-colors font-medium group">
            <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center mr-2 shadow-sm group-hover:shadow-md transition-all">
              <ArrowLeft className="w-4 h-4" />
            </div>
            Voltar para Home
          </Link>
          <div className="text-center mb-8">
            <h2 className="font-display font-bold text-3xl text-dark-900">
              Criar nova conta
            </h2>
            <p className="mt-2 text-dark-600">
              Junte-se à comunidade Cerveja Delivery
            </p>
          </div>
        </div>

        <div className="mt-2 sm:mx-auto sm:w-full sm:max-w-md relative z-10">
          <div className="bg-white py-10 px-4 shadow-large rounded-2xl sm:px-10 border border-gray-100">
            
            {/* Progress Steps */}
            <div className="flex items-center justify-center mb-8 space-x-2">
              {[1, 2, ...(role === 'brewery_owner' ? [3] : [])].map((s) => (
                <React.Fragment key={s}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${step >= s ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-500'}`}>
                    {s}
                  </div>
                  {((role === 'brewery_owner' && s < 3) || (role !== 'brewery_owner' && s < 2)) && (
                    <div className={`w-8 h-1 rounded-full transition-colors ${step > s ? 'bg-primary-500' : 'bg-gray-100'}`} />
                  )}
                </React.Fragment>
              ))}
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Step 1: Basic Info */}
              {step === 1 && (
                <div className="space-y-5 animate-fade-in-up">
                  <h3 className="text-lg font-semibold text-dark-900 mb-4">Seus Dados</h3>
                  <Input label="Nome Completo" name="full_name" required value={formData.full_name} onChange={handleChange} placeholder="Ex: João da Silva" />
                  <Input label="Email" type="email" name="email" required value={formData.email} onChange={handleChange} placeholder="seu@email.com" />
                  <Input label="Telefone / WhatsApp" name="phone" required value={formData.phone} onChange={handleChange} placeholder="(11) 99999-9999" />
                  <Input label="Senha" type="password" name="password" required value={formData.password} onChange={handleChange} placeholder="Mínimo 6 caracteres" />
                  <Input label="Confirmar Senha" type="password" name="confirmPassword" required value={formData.confirmPassword} onChange={handleChange} placeholder="Confirme sua senha" />
                  
                  <Button variant="primary" onClick={() => setStep(2)} className="w-full mt-4" disabled={!formData.email || !formData.password}>
                    Continuar
                  </Button>
                </div>
              )}

              {/* Step 2: Role Selection */}
              {step === 2 && (
                <div className="space-y-4 animate-fade-in-up">
                  <h3 className="text-lg font-semibold text-dark-900 text-center mb-4">Como você quer usar a plataforma?</h3>
                  
                  <button
                    type="button"
                    onClick={() => setRole('customer')}
                    className={`w-full p-4 border-2 rounded-xl transition-all text-left group relative overflow-hidden ${role === 'customer' ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500' : 'border-gray-200 hover:border-primary-300'}`}
                  >
                    <div className="flex items-center">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center mr-4 transition-colors ${role === 'customer' ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-500'}`}>
                        <User className="w-6 h-6" />
                      </div>
                      <div>
                        <span className="block font-bold text-dark-900 text-lg">Quero comprar</span>
                        <span className="text-sm text-dark-500">Encontre as melhores cervejas artesanais</span>
                      </div>
                      {role === 'customer' && <CheckCircle className="absolute top-4 right-4 text-primary-500 w-6 h-6" />}
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setRole('brewery_owner')}
                    className={`w-full p-4 border-2 rounded-xl transition-all text-left group relative overflow-hidden ${role === 'brewery_owner' ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500' : 'border-gray-200 hover:border-primary-300'}`}
                  >
                    <div className="flex items-center">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center mr-4 transition-colors ${role === 'brewery_owner' ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-500'}`}>
                        <Store className="w-6 h-6" />
                      </div>
                      <div>
                        <span className="block font-bold text-dark-900 text-lg">Sou uma Cervejaria</span>
                        <span className="text-sm text-dark-500">Venda seus produtos e gerencie pedidos</span>
                      </div>
                      {role === 'brewery_owner' && <CheckCircle className="absolute top-4 right-4 text-primary-500 w-6 h-6" />}
                    </div>
                  </button>

                  <div className="flex gap-3 mt-6">
                    <Button variant="secondary" onClick={() => setStep(1)} className="w-1/3">
                      Voltar
                    </Button>
                    {role === 'brewery_owner' ? (
                      <Button variant="primary" onClick={() => setStep(3)} className="w-2/3">
                        Continuar
                      </Button>
                    ) : (
                      <Button type="submit" variant="primary" loading={loading} className="w-2/3">
                        Criar Conta
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Step 3: Brewery Info */}
              {step === 3 && role === 'brewery_owner' && (
                <div className="space-y-5 animate-fade-in-up">
                  <div className="text-center mb-4">
                    <h3 className="text-lg font-semibold text-dark-900">Dados da Cervejaria</h3>
                    <p className="text-sm text-dark-500">Configure sua loja para começar</p>
                  </div>

                  <Input label="Nome da Cervejaria" name="brewery.name" required value={formData.brewery.name} onChange={handleChange} placeholder="Ex: Cervejaria Central" />
                  <Input label="Endereço Completo" name="brewery.address" required value={formData.brewery.address} onChange={handleChange} placeholder="Rua, Número, Bairro, Cidade - UF" />
                  <Input label="Telefone Comercial" name="brewery.phone" required value={formData.brewery.phone} onChange={handleChange} placeholder="(11) 3333-3333" />
                  
                  <div>
                    <label className="block text-sm font-medium text-dark-700 mb-2">Descrição</label>
                    <textarea 
                      name="brewery.description" 
                      rows={3} 
                      className="input-field" 
                      value={formData.brewery.description} 
                      onChange={handleChange}
                      placeholder="Conte um pouco sobre sua história e suas cervejas..." 
                    />
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800 flex items-start">
                    <AlertTriangle className="w-5 h-5 mr-2 flex-shrink-0" />
                    <span>
                      Ao criar a conta, você concorda com nossos termos de parceiro. Você receberá um email de confirmação.
                    </span>
                  </div>

                  <div className="flex gap-3 mt-6">
                    <Button variant="secondary" onClick={() => setStep(2)} className="w-1/3">
                      Voltar
                    </Button>
                    <Button type="submit" variant="primary" loading={loading} className="w-2/3">
                      Finalizar Cadastro
                    </Button>
                  </div>
                </div>
              )}
            </form>

            <div className="mt-8 pt-6 border-t border-gray-100 text-center">
              <p className="text-dark-500 text-sm">
                Já tem uma conta?{' '}
                <Link to="/login" className="font-semibold text-primary-600 hover:text-primary-500 transition-colors">
                  Faça login
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Register;